import { compact, each, map, pipe } from '@fxts/core';
import { html, type Html, rune, View } from 'rune-ts';
import { Button, Typo } from '@/ui/component';
import klass from './Popup.module.scss';

export interface PopupData {
  title: Typo;
  message: Typo | Html;
  okButton: Button;
  cancelButton?: Button;
}

export interface PopupOption {
  backdrop?: boolean;
  animation?: boolean;
  content_full?: boolean;
}

export class Popup extends View<PopupData> {
  constructor(
    data: PopupData,
    public option: PopupOption = {
      backdrop: false,
      animation: false,
      content_full: false,
    },
  ) {
    super(data);
  }

  private makeKlass() {
    const { backdrop } = this.option;

    return html`${backdrop ? klass.backdrop : ''}`;
  }

  override template() {
    const { title, message, okButton, cancelButton } = this.data;

    return html`<div class="${klass.popup} ${this.makeKlass()}">
      <div class="${klass.popup__window} ${klass.slide_in}">
        <div class="${klass.popup__header}">${title}</div>
        <div class="${klass.popup__content} ${this.option.content_full ? klass.content_full : ''}">${message}</div>
        <div class="${klass.popup__footer}">${cancelButton ?? ''}${okButton}</div>
      </div>
    </div> `;
  }

  override onRender() {
    document.body.appendChild(this.element());
  }

  public open() {
    this.render();

    return new Promise<'close' | 'cancel' | 'confirm'>((resolve, reject) => {
      const popup_window = this.element().querySelector(`.${klass.popup__window}`);
      this.element().classList.add(klass.open);

      this.data.okButton.element().focus();
      if (this.option.animation) {
        popup_window?.classList.add(klass.animate);
      }

      this.data.okButton.addEventListener('click', () => {
        resolve('confirm');
        this.close();
      });

      this.data.cancelButton?.addEventListener('click', () => {
        resolve('cancel');
        this.close();
      });

      this.element().addEventListener('click', (e) => {
        if (e.target === this.element()) {
          resolve('close');
          this.close();
        }
      });

      window.addEventListener('keydown', (e) => {
        if (e.code !== 'Escape') {
          return;
        }

        resolve('close');
        this.close();
      });
    });
  }

  public close() {
    const popup_window = this.element().querySelector(`.${klass.popup__window}`);

    const onEndAnimation = () => {
      this.element().classList.remove(klass.open);
      popup_window?.classList.remove(klass.animate, klass.slide_out);

      this.element().remove();

      popup_window?.removeEventListener('animationend', onEndAnimation);
    };
    popup_window?.addEventListener('animationend', onEndAnimation);

    popup_window?.classList.add(this.option.animation ? klass.animate : null, klass.slide_out);

    if (!this.option.animation) {
      this.element().remove();
    }
  }

  static closeAll() {
    const $popups = document.querySelectorAll(`.${klass.popup}`);

    pipe(
      $popups,
      map(($popup) => rune.get($popup, Popup)),
      compact,
      each((popup) => {
        const popup_window = popup.element().querySelector(`.${klass.popup__window}`);

        const onEndAnimation = () => {
          popup.element().classList.remove(klass.open);
          popup_window?.classList.remove(klass.animate, klass.slide_out);

          popup.element().remove();

          popup_window?.removeEventListener('animationend', onEndAnimation);
        };
        popup_window?.addEventListener('animationend', onEndAnimation);

        popup_window?.classList.add(popup.option.animation ? klass.animate : null, klass.slide_out);

        if (!popup.option.animation) {
          popup.element().remove();
        }
      }),
    );
  }

  static async open(
    data: PopupData,
    option: PopupOption = {
      backdrop: false,
      animation: false,
    },
  ) {
    const popup = new Popup(data, option);
    return popup.open();
  }
}
